import React, { useEffect, useRef, useState } from 'react';

const CheckboxMultiSelectFilter = ({ id, items, selectedIds, onChange, allLabel, pluralNoun }) => {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const selectAllRef = useRef(null);

    const sortedItems = [...items].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
    const visibleItems = searchTerm
        ? sortedItems.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
        : sortedItems;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const allIds = items.map((item) => String(item.id));
    const allSelected = selectedIds.length > 0 && selectedIds.length === allIds.length;
    const someSelected = selectedIds.length > 0 && selectedIds.length < allIds.length;

    const visibleIds = visibleItems.map((item) => String(item.id));
    const visibleAllSelected = visibleIds.length > 0 && visibleIds.every((v) => selectedIds.includes(v));
    const visibleSomeSelected = visibleIds.some((v) => selectedIds.includes(v)) && !visibleAllSelected;

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = visibleSomeSelected;
        }
    }, [visibleSomeSelected]);

    const toggleId = (id) => {
        const idStr = String(id);
        const next = selectedIds.includes(idStr)
            ? selectedIds.filter((v) => v !== idStr)
            : [...selectedIds, idStr];
        onChange(next);
    };

    const toggleSelectAll = () => {
        onChange(
            visibleAllSelected
                ? selectedIds.filter((v) => !visibleIds.includes(v))
                : [...new Set([...selectedIds, ...visibleIds])]
        );
    };

    let label = allLabel;
    if (selectedIds.length === 1) {
        const match = items.find((item) => String(item.id) === selectedIds[0]);
        label = match ? match.name : '1 selected';
    } else if (someSelected) {
        label = `${selectedIds.length} ${pluralNoun} selected`;
    }

    return (
        <div style={{ position: 'relative' }} ref={containerRef}>
            <button
                type="button"
                id={id}
                className="form-control text-left d-flex justify-content-between align-items-center"
                onClick={() => {
                    setOpen((prev) => !prev);
                    setSearchTerm('');
                }}
            >
                <span>{label}</span>
                <i className={`fas fa-caret-${open ? 'up' : 'down'} ml-2`}></i>
            </button>
            {open && (
                <div
                    className="p-2"
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '2px',
                        minWidth: '100%',
                        maxHeight: '240px',
                        overflowY: 'auto',
                        backgroundColor: '#fff',
                        border: '1px solid rgba(0, 0, 0, .15)',
                        borderRadius: '0.25rem',
                        boxShadow: '1px 2px 3px rgba(0, 0, 0, .125)',
                        zIndex: 1000,
                    }}
                >
                    <input
                        type="text"
                        className="form-control form-control-sm mb-2"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                    />
                    <label className="dropdown-item d-flex align-items-center mb-0 font-weight-bold" style={{ cursor: 'pointer', paddingLeft: '0.5rem', color: '#000' }}>
                        <input
                            type="checkbox"
                            className="mr-2"
                            ref={selectAllRef}
                            checked={visibleAllSelected}
                            onChange={toggleSelectAll}
                            disabled={!visibleIds.length}
                        />
                        Select all
                    </label>
                    <div className="dropdown-divider"></div>
                    {visibleItems.map((item) => (
                        <label key={item.id} className="dropdown-item d-flex align-items-center mb-0" style={{ cursor: 'pointer', paddingLeft: '0.5rem', color: '#000' }}>
                            <input
                                type="checkbox"
                                className="mr-2"
                                checked={selectedIds.includes(String(item.id))}
                                onChange={() => toggleId(item.id)}
                            />
                            {item.name}
                        </label>
                    ))}
                    {!visibleItems.length && <div className="text-muted small" style={{ paddingLeft: '0.5rem' }}>No matches</div>}
                </div>
            )}
        </div>
    );
};

const DashboardFilters = ({ customers, jobTypes, filters, onChange }) => {
    return (
    <div className="d-flex flex-wrap align-items-end" style={{ gap: '1rem' }}>
        <div className="form-group mb-0" style={{ minWidth: '220px' }}>
            <label htmlFor="dashboardFilterCustomer" className="mb-1">Customer</label>
            <CheckboxMultiSelectFilter
                id="dashboardFilterCustomer"
                items={customers}
                selectedIds={(filters.customerIds || []).map(String)}
                onChange={(customerIds) => onChange({ ...filters, customerIds })}
                allLabel="All customers"
                pluralNoun="customers"
            />
        </div>

        <div className="form-group mb-0" style={{ minWidth: '220px' }}>
            <label htmlFor="dashboardFilterJobType" className="mb-1">Job type</label>
            <CheckboxMultiSelectFilter
                id="dashboardFilterJobType"
                items={jobTypes}
                selectedIds={(filters.jobTypeIds || []).map(String)}
                onChange={(jobTypeIds) => onChange({ ...filters, jobTypeIds })}
                allLabel="All job types"
                pluralNoun="job types"
            />
        </div>

        <div className="text-muted small">
            <i className="fas fa-info-circle"></i> Data refreshes every 5 minutes
        </div>
    </div>
    );
};

export default DashboardFilters;
